import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number with locale-aware separators and optional decimal places.
 */
export function formatNumber(value: number, decimals = 0): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a number as compact (1.2K, 3.4M, etc.)
 */
export function formatCompact(value: number): string {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Format a timestamp (ms) as a readable date.
 */
export function formatDate(timestamp: number, style: "short" | "long" = "short"): string {
  const date = new Date(timestamp);
  if (style === "long") {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * Format a timestamp as relative time (2m ago, 3h ago, etc.)
 */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(timestamp);
}

/**
 * Format a delta with + or - sign and color class name.
 */
export function formatDelta(value: number): { text: string; className: string } {
  if (value > 0) return { text: `+${value}`, className: "text-success" };
  if (value < 0) return { text: `${value}`, className: "text-danger" };
  return { text: "0", className: "text-text-3" };
}

/**
 * Safely display a value, replacing null/undefined/NaN/empty with a fallback.
 */
export function displayValue(value: unknown, fallback = "\u2014"): string {
  if (value == null) return fallback;
  if (typeof value === "number" && isNaN(value)) return fallback;
  if (typeof value === "string" && value.trim() === "") return fallback;
  return String(value);
}
